(function () {
  const VARIANT_UPDATE = "variant:update";

  const CLEANUP_KEY = "__dapPdpCleanup";

  /** Match Admin API inventory map keys (numeric id, full GID, or legacy suffix). */
  function pickInventoryItem(inventory, variantId) {
    if (!inventory || variantId == null || variantId === "") return undefined;
    const n = String(variantId).trim();
    if (inventory[n]) return inventory[n];
    const gid = n.startsWith("gid://")
      ? n
      : `gid://shopify/ProductVariant/${n}`;
    if (inventory[gid]) return inventory[gid];
    const suffix = gid.split("/").pop();
    if (suffix && inventory[suffix]) return inventory[suffix];
    return undefined;
  }

  function escapeCssAttr(value) {
    const s = String(value);
    if (typeof CSS !== "undefined" && typeof CSS.escape === "function") {
      return CSS.escape(s);
    }
    return s.replace(/\\/g, "\\\\").replace(/"/g, '\\"');
  }

  function init() {
    const prev = globalThis[CLEANUP_KEY];
    if (typeof prev === "function") prev();

    const root = document.querySelector("[data-dap-pdp]");
    if (!root) return;

    const gatingEnabled = root.dataset.inventoryGatingEnabled === "true";
    if (!gatingEnabled) return;

    if (!window.DAP) return;

    let buttons = [...root.querySelectorAll("[data-dap-add-to-cart]")];
    const productId = root.dataset.productId;
    if (productId) {
      const stickyAtc = document.querySelector(
        `sticky-add-to-cart[data-product-id="${escapeCssAttr(productId)}"] [data-dap-add-to-cart]`,
      );
      if (stickyAtc && !buttons.includes(stickyAtc)) {
        buttons.push(stickyAtc);
      }
    }
    const msg = root.querySelector("[data-dap-pdp-message]");
    const selectCta = root.querySelector("[data-dap-select-cta]");
    const atcGroup = root.querySelector("[data-dap-atc-group]");
    const unavailable = root.querySelector("[data-dap-pdp-unavailable]");
    const unavailableChange = root.querySelector(
      "[data-dap-pdp-unavailable-change]",
    );
    if (buttons.length === 0 && !selectCta) return;

    const t = (key) =>
      window.__DAP_I18N?.[key] ??
      {
        pdp_pick_first: "Choose date and address first",
        pdp_checking: "Checking availability...",
        pdp_unavailable: "Not available for selected date",
        pdp_check_failed: "Could not check availability",
      }[key];

    function shouldSkipGating() {
      return (
        root.getAttribute("data-requires-shipping") === "false" ||
        root.getAttribute("data-inventory-management") === "" ||
        root.getAttribute("data-inventory-policy") === "continue"
      );
    }

    let fetchController = null;

    const unsub = window.DAP.onSessionChange(() => {
      if (shouldSkipGating()) return;
      checkInventory();
    });

    function setPrimaryButtonLabel(mode) {
      const addLabel = root.dataset.dapAtcLabel;
      const soldLabel = root.dataset.dapSoldOutLabel;
      const text =
        mode === "sold_out" ? soldLabel : mode === "add" ? addLabel : null;
      if (!text) return;
      for (const btn of buttons) {
        const inner = btn.querySelector(".add-to-cart-text__content span span");
        if (inner) {
          inner.textContent = text;
          continue;
        }
        const row = btn.querySelector(".add-to-cart-text__content");
        if (row) row.textContent = text;
      }
    }

    function setButtonsDisabled(disabled) {
      for (const btn of buttons) {
        btn.disabled = disabled;
      }
    }

    function setMode(mode) {
      const showSelectCta = mode === "select-delivery";
      const showAtcGroup = mode === "available" || mode === "checking";
      const showUnavailable = mode === "unavailable";

      if (selectCta) selectCta.hidden = !showSelectCta;
      if (atcGroup) atcGroup.hidden = !showAtcGroup;
      if (unavailable) unavailable.hidden = !showUnavailable;
    }

    function abortPending() {
      fetchController?.abort();
      fetchController = null;
    }

    async function checkInventory() {
      if (shouldSkipGating()) {
        abortPending();
        setButtonsDisabled(false);
        setPrimaryButtonLabel("add");
        setMode("available");
        if (msg) msg.textContent = "";
        return;
      }

      const currentSession = window.DAP.getSession();
      if (!currentSession || !window.DAP.isSessionValid()) {
        abortPending();
        setButtonsDisabled(true);
        setPrimaryButtonLabel("add");
        setMode("select-delivery");
        if (msg) msg.textContent = "";
        return;
      }

      const variantId = root.dataset.variantId;
      if (!variantId) return;

      const locationId = currentSession.location_id ?? currentSession.location;
      if (!locationId) {
        abortPending();
        setButtonsDisabled(true);
        setPrimaryButtonLabel("add");
        setMode("select-delivery");
        if (msg) msg.textContent = "";
        return;
      }

      fetchController?.abort();
      fetchController = new AbortController();
      const { signal } = fetchController;

      setButtonsDisabled(true);
      setMode("checking");
      if (msg) msg.textContent = t("pdp_checking");

      try {
        const resp = await fetch(
          `/apps/delivery/inventory?location_id=${encodeURIComponent(locationId)}&variant_ids=${encodeURIComponent(variantId)}`,
          { signal },
        );
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const { inventory } = await resp.json();

        const item = pickInventoryItem(inventory, variantId);
        if (!item || !item.tracked || item.qty > 0) {
          setButtonsDisabled(false);
          setPrimaryButtonLabel("add");
          setMode("available");
          if (msg) msg.textContent = "";
        } else {
          setButtonsDisabled(true);
          setPrimaryButtonLabel("sold_out");
          setMode("unavailable");
          if (msg) msg.textContent = "";
        }
      } catch (err) {
        if (err?.name === "AbortError") return;
        setButtonsDisabled(false);
        setPrimaryButtonLabel("add");
        setMode("available");
        if (msg) msg.textContent = t("pdp_check_failed");
      }
    }

    function onUnavailableChange() {
      const dialog = document.getElementById("delivery-dialog");
      if (dialog && typeof dialog.showDialog === "function") {
        dialog.showDialog();
      }
    }

    if (unavailableChange) {
      unavailableChange.addEventListener("click", onUnavailableChange);
    }

    function onVariantUpdate(e) {
      const resource = e.detail?.resource;
      if (!resource || resource.id == null) return;

      root.dataset.variantId = String(resource.id);

      if (resource.requires_shipping !== undefined) {
        root.dataset.requiresShipping = String(resource.requires_shipping);
      }
      const invMgmt = resource.inventory_management;
      if (invMgmt !== undefined) {
        root.dataset.inventoryManagement =
          invMgmt === "shopify" || invMgmt === true ? "shopify" : "";
      }
      if (resource.inventory_policy !== undefined) {
        root.dataset.inventoryPolicy = String(resource.inventory_policy);
      }

      checkInventory();
    }

    function onDapVariantChange(e) {
      const detail = e.detail || {};
      if (detail.variantId) root.dataset.variantId = detail.variantId;
      if (detail.requiresShipping !== undefined)
        root.dataset.requiresShipping = String(detail.requiresShipping);
      if (detail.inventoryManagement !== undefined)
        root.dataset.inventoryManagement = detail.inventoryManagement;
      if (detail.inventoryPolicy !== undefined)
        root.dataset.inventoryPolicy = detail.inventoryPolicy;
      checkInventory();
    }

    root.addEventListener(VARIANT_UPDATE, onVariantUpdate);
    root.addEventListener("dap:variant-change", onDapVariantChange);

    if (shouldSkipGating()) {
      setButtonsDisabled(false);
      setPrimaryButtonLabel("add");
      setMode("available");
      if (msg) msg.textContent = "";
    } else {
      checkInventory();
    }

    function cleanup() {
      if (unsub) unsub();
      fetchController?.abort();
      fetchController = null;
      root.removeEventListener(VARIANT_UPDATE, onVariantUpdate);
      root.removeEventListener("dap:variant-change", onDapVariantChange);
      if (unavailableChange) {
        unavailableChange.removeEventListener("click", onUnavailableChange);
      }
      if (globalThis[CLEANUP_KEY] === cleanup) {
        delete globalThis[CLEANUP_KEY];
      }
    }

    globalThis[CLEANUP_KEY] = cleanup;

    return cleanup;
  }

  init();
})();
